// components/portal/BrandShortlistDetail.tsx
'use client';

import { activeCampaignResearch, researchingFieldsOf, researchStateOf, campaignSelection } from '#lib/leasing/researchPass';
import { readCampaignRow as readResearchCampaign } from '#lib/leasing/campaignRow';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { BrandMark } from '@/components/ui/BrandMark';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { BrandIdFiche } from '@/components/portal/BrandIdFiche';
import { StoresMapPanel, type StoreLocation } from '@/components/portal/StoresMapPanel';
import { BrandRevenueChart } from '@/components/portal/BrandRevenueChart';
import { EnrichProgressBar } from '@/components/portal/EnrichProgressBar';
import { BrandExpansionChart } from '@/components/portal/BrandExpansionChart';
import { ContactsTable } from '@/components/entities/tables/contacts/ContactsTable';
import { ContactCardLoading, RecommendedContactCard } from '@/components/portal/RecommendedContactCard';
import { BrandGallery, BrandOverviewHero } from '@/components/portal/BrandStoreGallery';
import { BrandNewsTab } from '@/components/portal/BrandNewsTab';
import { BrandGoogleSummary } from '@/components/portal/BrandGoogleSummary';
import { BrandPricesTab } from '@/components/portal/BrandPricesTab';
import { SkeletonRows } from '@/components/ui/Skeleton';
import type { LeasingBrandDTO } from '#types/types';

// Every tab's content starts on the column's left edge, as the tab row does.
const DEEP_TAB = 'py-4';

type DetailTab = 'summary' | 'verdict' | 'scoring' | 'prices' | 'expansion' | 'stores' | 'gallery' | 'news' | 'revenue' | 'contacts';

// Where each researched field lives on the card; the rest are rows of the Summary fiche.
const ENRICH_TAB: Partial<Record<string, DetailTab>> = { stores: 'stores', financials: 'revenue', contacts: 'contacts' };

const LABELS: Record<'en' | 'fr', Record<Exclude<DetailTab, 'verdict'>, string> & { why: string; verdict: string }> = {
  en: { summary: 'Overview', scoring: 'Scoring', prices: 'Prices', expansion: 'Expansion', stores: 'Stores', gallery: 'Gallery', news: 'News', revenue: 'Revenue', contacts: 'Contacts', why: 'Why it was picked', verdict: 'Verdict' },
  fr: { summary: 'Aperçu', scoring: 'Scoring', prices: 'Prix', expansion: 'Expansion', stores: 'Magasins', gallery: 'Galerie', news: 'Actualités', revenue: 'Revenus', contacts: 'Contacts', why: 'Pourquoi cette marque', verdict: 'Verdict' },
};

// Tabs drawn from the brand's full detail (photos, stores, accounts, contacts). Until it lands they
// show their own shape in a shimmer: the list projection leaves those fields empty, and an empty
// field reads as "no revenue" or "no stores" when it only means "not loaded yet".
const DETAIL_TABS = new Set<DetailTab>(['summary', 'prices', 'stores', 'gallery', 'revenue', 'contacts']);

const TabSkeleton = ({ tab }: { tab: DetailTab }) => (
  <div className={`${DEEP_TAB} skeleton-in space-y-6`} aria-busy="true" role="status" aria-label="Loading">
    {tab === 'summary' ? (
      <>
        <div className="grid grid-cols-4 gap-2">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton aspect-[4/3] rounded-[16px]" />)}</div>
        <div className="space-y-2.5">{['38%', '92%', '84%', '60%'].map((w) => <div key={w} className="skeleton h-3 rounded-full" style={{ width: w }} />)}</div>
      </>
    ) : tab === 'revenue' ? (
      <>
        <div className="grid grid-cols-4 gap-2">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-[92px] rounded-[16px]" />)}</div>
        <div className="skeleton h-[300px] rounded-[20px]" />
      </>
    ) : tab === 'prices' ? (
      <>
        <div className="flex gap-2">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-[76px] flex-1 rounded-[16px]" />)}</div>
        <div className="grid grid-cols-3 gap-3">{[0, 1, 2].map((i) => <div key={i} className="skeleton aspect-square rounded-[16px]" />)}</div>
      </>
    ) : tab === 'gallery' ? (
      <div className="grid grid-cols-3 gap-2">{Array.from({ length: 9 }, (_, i) => <div key={i} className="skeleton aspect-[4/3] rounded-[16px]" />)}</div>
    ) : tab === 'stores' ? (
      <div className="skeleton h-[460px] rounded-[20px]" />
    ) : (
      <SkeletonRows rows={4} />
    )}
  </div>
);

/**
 * The brand detail, the same in Data and in Source (Lead / PortfolioFit rows, the Brand page).
 * REBUILT FROM FIRST PRINCIPLES (owner, 2026-09-30: 'maximum readability'). What a reader needs
 * first is on the Overview, in reading order: the judgement (the Source verdict, or the Fater
 * score in Data), what the brand does, then its facts. The deep dives (Scoring, Expansion,
 * Stores, Revenue, Contacts) are one row of plain tabs after it. 'Summary' and 'Verdict' used to
 * be two tabs; both now open the Overview, so the agent's old tab names still land.
 */
export const BrandShortlistDetail = ({
  brandId,
  lang,
  scoring,
  summary,
  headline,
  withHeader = false,
}: {
  brandId: string;
  lang: 'en' | 'fr';
  /** Fit-specific scoring panel. Omit for a plain brand (the Scoring tab is then hidden). */
  scoring?: React.ReactNode;
  summary?: React.ReactNode;
  /** Replaces the verdict at the head of the Overview (Data: the Fater score). */
  headline?: React.ReactNode;
  /** Logo and name above the tabs, for a page with no row above it. */
  withHeader?: boolean;
}) => {
  const { kernel } = useKernel();
  const brand = useKernelStore((state) => (brandId ? state.inventory.Brand.registry[brandId] : undefined)) as
    | Partial<LeasingBrandDTO>
    | undefined;
  const otherContactIds = (brand?.contactIds ?? []).filter((id) => id !== brand?.recommendedContact?.contactId);
  // THE AGENT CAN OPEN A TAB. This was plain React state, which meant "get me the contacts for X"
  // could fetch 14 contacts and leave the user staring at the Summary tab, with the result
  // announced in chat and invisible on screen. The kernel value wins whenever it is set; clicking
  // a tab clears it so the user is never fighting a stale agent instruction.
  const [localTab, setLocalTab] = useState<DetailTab>('summary');
  const kernelTab = useKernelStore((state) => state.sourceBrandDetailTab);
  const detailTab: DetailTab = kernelTab ?? localTab;
  // A research pass shows nothing for a brand it has not reached: its stored data is what is being replaced.
  const researchState = useKernelStore((state) => researchStateOf(activeCampaignResearch(readResearchCampaign(state.inventory, state.sourceSelectedCampaignId)), brandId));
  // Why the run picked this brand, above the Judge's verdict: the pick is a judgement too.
  const pickReason = useKernelStore((state) => campaignSelection(readResearchCampaign(state.inventory, state.sourceSelectedCampaignId))?.reasons[brandId] ?? null);
  const setDetailTab = (next: DetailTab) => {
    setLocalTab(next);
    kernel.current?.setState({ sourceBrandDetailTab: null });
  };

  // FOLLOW THE RESEARCH (owner, 2026-09-26): the card opens the tab holding the field being
  // researched now and scrolls to it. The latest field marked wins; an enrich-all marks every
  // field at once and stays on the Summary.
  const enrichingDim = useKernelStore((state) => {
    const dims = brandId ? researchingFieldsOf(state.sourceEnrichingDims[brandId], activeCampaignResearch(readResearchCampaign(state.inventory, state.sourceSelectedCampaignId)), brandId) : undefined;
    if (!dims?.length) return null;
    return new Set(dims).size > 3 ? 'all' : dims[dims.length - 1];
  });
  const detailRootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!enrichingDim) return;
    kernel.current?.setState({ sourceBrandDetailTab: ENRICH_TAB[enrichingDim] ?? 'summary' });
    const frame = requestAnimationFrame(() => {
      const root = detailRootRef.current;
      (root?.querySelector(`[data-enrich-field="${enrichingDim}"]`) ?? root)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [kernel, enrichingDim]);

  const measuredDetailRef = useRef<{ brandId: string; detailTab: DetailTab } | null>(null);
  useEffect(() => {
    const observe = () => {
      if (!brandId || document.visibilityState !== 'visible') return;
      const previous = measuredDetailRef.current;
      if (previous?.brandId === brandId && previous.detailTab === detailTab) return;
      const recorded = kernel.current?.beginSourceAction?.({
        action: previous?.brandId === brandId ? 'tab' : 'navigation', origin: 'observed',
        data: { entityType: 'Brand', entityId: brandId, tab: detailTab.toUpperCase() },
      });
      if (recorded) measuredDetailRef.current = { brandId, detailTab };
    };
    observe();
    document.addEventListener('visibilitychange', observe);
    return () => document.removeEventListener('visibilitychange', observe);
  }, [brandId, detailTab]);

  // Pull the FULL brand detail into the registry so the fiche + stores/revenue/contacts tabs
  // have data. The brands table hydrates the registry from the LIST endpoint, whose projection
  // omits the heavy `locations` array, so a cached brand can exist with no locations, and the
  // Stores map would render empty. Trigger the detail fetch whenever locations aren't loaded,
  // not just when the brand is entirely absent. A per-brand ref guards against a refetch loop.
  const detailFetchedRef = useRef<Set<string>>(new Set());
  const [syncedId, setSyncedId] = useState<string | null>(null);
  // THE FINANCIAL SERIES TOO (2026-09-30): the admin Brands quality review hydrates from a list
  // projection that carries the stores but not the financial records, so a brand with nine filed
  // years (Action, EUR 5.27bn in 2024, VERIFIED) showed 'No verified revenue' on its Revenue tab.
  // `verifiedFinancials` is undefined exactly when the records were not requested.
  useEffect(() => {
    if (!brandId) return;
    if (detailFetchedRef.current.has(brandId)) return;
    detailFetchedRef.current.add(brandId);
    void Promise.resolve(kernel.current?.syncEntity('Brand', brandId, { force: true })).finally(() => setSyncedId(brandId));
  }, [brandId, brand, kernel]);
  // The detail is in when its records are (undefined exactly until then), or once the fetch settled:
  // a failed fetch shows what the registry holds rather than shimmering forever.
  const detailLoaded = brand?.verifiedFinancials !== undefined || syncedId === brandId;

  // ChatGPT's tab row: one lighter pill that slides to the open tab, rather than jumping.
  const tabRefs = useRef<Map<DetailTab, HTMLButtonElement>>(new Map());
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null);

  const t = LABELS[lang] ?? LABELS.en;
  const TABS: Array<{ id: DetailTab; label: string }> = [
    { id: 'summary', label: t.summary },
    // Scoring is fit-specific (Source) or the Fater score's criteria (Data); hidden without one.
    ...(scoring ? [{ id: 'scoring' as const, label: t.scoring }] : []),
    { id: 'prices', label: t.prices },
    { id: 'expansion', label: t.expansion },
    { id: 'stores', label: t.stores },
    { id: 'gallery', label: t.gallery },
    { id: 'news', label: t.news },
    { id: 'revenue', label: t.revenue },
    { id: 'contacts', label: t.contacts },
  ];
  const shownTab: DetailTab = detailTab === 'verdict' || (detailTab === 'scoring' && !scoring) ? 'summary' : detailTab;
  useLayoutEffect(() => {
    const button = tabRefs.current.get(shownTab);
    setPill(button ? { left: button.offsetLeft, width: button.offsetWidth } : null);
  }, [shownTab, lang, !!scoring]);

  if (researchState === 'waiting') {
    return (
      <div className="p-6 text-center text-white/40" style={{ fontSize: 13 }}>
        {lang === 'fr' ? "En attente de recherche. Les données de cette marque apparaissent une fois reconstruites par l'agent." : "Waiting for research. This brand's data appears once the agent has rebuilt it."}
      </div>
    );
  }

  return (
    // Keyed on the brand so opening a DIFFERENT brand replays the animation. Without the key the
    // panel is reused across rows and the second brand you click appears instantly while the first
    // one faded in - the same interaction behaving two ways.
    // ONE CENTRED READING COLUMN (owner, 2026-09-30: in Data 'everything looks stuck to the left'):
    // on a wide screen the detail reads like a ChatGPT conversation, 760px in the middle, every tab
    // and every title on the same left edge. A narrow Source column simply fills it.
    <div key={brandId} ref={detailRootRef} className="@container relative mx-auto flex min-h-full w-full max-w-[784px] flex-col px-3 pb-6 pt-3 brand-detail-open">
      {/* The same Egyptian-blue slide as the view's top edge: the panel whose data is being
          written says so itself, instead of asking the eye to find a 3px bar a screen away. */}
      <EnrichProgressBar />
      {withHeader && (
        <div className="mb-4 flex items-center gap-3 pt-1">
          {brand?.logoUrl ? <BrandMark logoUrl={brand.logoUrl} name={brand.name} size={40} radius={10} /> : null}
          <h2 className="min-w-0 truncate font-semibold text-white" style={{ fontSize: 22, lineHeight: '28px' }}>{brand?.name ?? ''}</h2>
        </div>
      )}
      {/* One row of plain tabs, ChatGPT's: words only, the open one on a lighter pill. It scrolls
          sideways rather than wrapping onto a second line in a narrow column; in a side panel the tabs share
          the width evenly and step down a size, so all five show without scrolling. */}
      <div role="tablist" className="hide-scrollbar relative mb-2 flex items-center gap-1 overflow-x-auto [--tab-fs:14px] @max-[480px]:gap-0.5 @max-[480px]:[--tab-fs:13px]">
        {pill ? <span aria-hidden className="brand-tab-pill pointer-events-none absolute top-0 h-8 rounded-full bg-white/[0.08]" style={{ left: pill.left, width: pill.width }} /> : null}
        {TABS.map((tb) => (
          <button
            key={tb.id}
            ref={(node) => { if (node) tabRefs.current.set(tb.id, node); else tabRefs.current.delete(tb.id); }}
            type="button"
            role="tab"
            aria-selected={shownTab === tb.id}
            aria-pressed={shownTab === tb.id}
            onClick={() => setDetailTab(tb.id)}
            className={clsx(
              'relative inline-flex h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-full px-3 transition-colors duration-200 @max-[480px]:flex-1 @max-[480px]:px-1.5',
              shownTab === tb.id ? 'text-white' : 'text-[#AFAFAF] hover:bg-white/[0.05] hover:text-white',
            )}
            style={{ fontSize: 'var(--tab-fs)' }}
          >
            {tb.label}
          </button>
        ))}
      </div>
      {/* Keyed on the tab and on the detail's arrival: switching tabs, or the data replacing its
          skeleton, fades the new content in instead of snapping it. */}
      <div key={`${shownTab}:${detailLoaded}`} className="brand-tab-in flex flex-1 flex-col">
      {!detailLoaded && DETAIL_TABS.has(shownTab) ? (
        <TabSkeleton tab={shownTab} />
      ) : shownTab === 'summary' ? (
        <div className={`${DEEP_TAB} space-y-8`}>
          <BrandOverviewHero name={brand?.name ?? ''} logoUrl={brand?.logoUrl} photos={brand?.storeGallery} onOpenGallery={() => setDetailTab('gallery')} lang={lang} />
          {headline ?? ((pickReason || summary) ? (
            <section aria-label={t.verdict}>
              <h3 className="font-semibold text-[#ECECEC]" style={{ fontSize: 16, lineHeight: '24px' }}>{t.verdict}</h3>
              {pickReason ? <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 14, lineHeight: '20px' }}>{t.why}: {pickReason}</p> : null}
              {summary ? <div className="mt-2 max-w-[720px] text-[#D0D0D0]" style={{ fontSize: 15, lineHeight: '24px' }}>{summary}</div> : null}
            </section>
          ) : null)}
          <BrandGoogleSummary brandId={brandId} lang={lang} />
          {/* undefined: the detail that names the person is still on its way; null: nobody picked. */}
          {brand?.recommendedContact ? <RecommendedContactCard compact recommendation={brand.recommendedContact} lang={lang} onOpen={() => setDetailTab('contacts')} />
            : brand?.recommendedContact === undefined ? <ContactCardLoading /> : null}
          <BrandIdFiche brandId={brandId} lang={lang} />
        </div>
      ) : shownTab === 'scoring' ? (
        <>{scoring}</>
      ) : shownTab === 'prices' ? (
        <div className={DEEP_TAB}><BrandPricesTab brand={brand} lang={lang} /></div>
      ) : shownTab === 'expansion' ? (
        <div className={DEEP_TAB}><BrandExpansionChart brandId={brandId} lang={lang} /></div>
      ) : shownTab === 'stores' ? (
        // The map runs to the bottom of the panel (owner, 2026-10-02), never shorter than 460px.
        <div className="relative my-4 min-h-[460px] flex-1">
          <div className="absolute inset-0">
            <StoresMapPanel locations={(brand?.locations as StoreLocation[]) ?? []} brandName={brand?.name} brandLogoUrl={brand?.logoUrl} lang={lang} brandId={brandId || undefined} />
          </div>
        </div>
      ) : shownTab === 'gallery' ? (
        <div className={DEEP_TAB}><BrandGallery name={brand?.name ?? ''} photos={brand?.storeGallery} lang={lang} /></div>
      ) : shownTab === 'news' ? (
        <div className={DEEP_TAB}><BrandNewsTab brandId={brandId} lang={lang} /></div>
      ) : shownTab === 'revenue' ? (
        <div className={DEEP_TAB}><BrandRevenueChart brandId={brandId || undefined} lang={lang} /></div>
      ) : (
        <>
          {brand?.recommendedContact ? <RecommendedContactCard recommendation={brand.recommendedContact} lang={lang} /> : null}
          {/* The main contact is the card above; the list holds the others, and says so. */}
          {otherContactIds.length > 0 || !brand?.recommendedContact ? (
            <>
              {brand?.recommendedContact ? <h3 className="px-6 pb-1 pt-5 font-semibold text-[#ECECEC]" style={{ fontSize: 16 }}>{lang === 'fr' ? 'Autres contacts' : 'Other contacts'}</h3> : null}
              <ContactsTable
                hideToolbar
                brandId={brandId || undefined}
                scopedIds={otherContactIds.length > 0 ? otherContactIds : undefined}
              />
            </>
          ) : null}
        </>
      )}
      </div>
    </div>
  );
};

BrandShortlistDetail.displayName = 'BrandShortlistDetail';
